import type { Response } from '@/types/common';
import OSS from 'ali-oss';
import { message } from 'ant-design-vue';
import { v4 as uuidv4, validate } from 'uuid';
import httpUtil from './http';
import { buildOssDownloadResponse } from './oss-download-response';
import { putWithStrategy, type OssLikeClient } from './oss-upload';

const { dft, mfd } = httpUtil;

export type OssKey = 'ctc' | 'ctc-private';
export enum OssCategory {
  // 上传文件的分类，用于文件夹分类
  NoCategory = 'no-category', // 无分类，尽量别用
  Avatar = 'avatar', // 头像
  Issue = 'issue',
  AfterSalesProblem = 'After-sales-problem',
  ProductionOther = 'production-other', // 生产其他目录:TR评审
  ProductionInspect = 'production-inspect', // 生产检验单目录
  ProductionIssue = 'production-issue', // 生产问题单目录
  ProductionSpecification = 'production-specification', // 生产检验规范目录
  ProductionSample = 'production-sample', // 生产样品目录
  DevReliability = 'dev-reliability', // 开发 - 可靠性测试目录
  DevOrt = 'dev-ort', // 开发 - ORT测试目录
  SupplierDeliveryInspect = 'supplier/delivery/inspect', // 供应商交付检验单目录
  SupplierDeliveryInspectFunctionality = 'supplier/delivery/inspect/functionality', // 供应商交付检验单 - 功能项全检目录
  AiRobotChat = 'ai-robot-chat', // CTC 机器人 chatBot 用户上传图片留存目录（私有桶）
}

const dftOssKey = 'ctc-private';
const SIGNATURE_EXPIRES = 3600; // 加密 url 过期时间

class OssUtil {
  utoken: string; // passport utoken

  constructor() {
    this.utoken = '';
  }

  /**
   * @description 文件名转换
   */
  encodeFileName(fileName: string, category: OssCategory) {
    return `${category}/${uuidv4()}_${encodeURIComponent(fileName)}`;
  }

  /**
   * @description 文件名解析
   */
  parseFileName(fileName: string) {
    const arr = fileName.split('_');
    if (validate(arr[0]) && arr.length > 1) {
      try {
        return decodeURIComponent(arr.slice(1).join('_'));
      } catch (e) {
        console.warn('Error decoding file name:', e);
        return arr.slice(1).join('_');
      }
    }
    try {
      return decodeURIComponent(fileName);
    } catch (e) {
      console.warn('Error decoding file name:', e);
      return fileName;
    }
  }

  /**
   * @description 获取 passport utoken
   */
  async getUtoken() {
    if (this.utoken) {
      return;
    }
    const resp = (await mfd.get('/api/v1/ossToken')) as Response;
    if (resp.code === 0) {
      this.utoken = resp.data.token;
      return;
    }
    message.error(resp.data.msg);
    throw new Error('Get UToken Failed');
  }

  /**
   * @description 获取 OSS 配置
   */
  async getOssConfig(ossKey?: OssKey) {
    const host = `${import.meta.env.VITE_OSS_BASE_URL}/v1/ststoken/${ossKey ?? dftOssKey}`;
    let res = await dft.get(host, {
      headers: {
        'x-auth-token': this.utoken,
      },
    });
    if (!res.data.data) {
      // 可能由于浏览器对页面的缓存，导致 utoken 失效，进行一次尝试重新获取
      this.utoken = '';
      await this.getUtoken();
      res = await dft.get(host, {
        headers: {
          'x-auth-token': this.utoken,
        },
      });
    }
    return res.data;
  }

  /**
   * @description 上传文件
   */
  async uploadFile(file: File, category: OssCategory, ossKey?: OssKey, onProgress?: (percent: number) => void) {
    await this.getUtoken();
    const configs = await this.getOssConfig(ossKey);
    const { data } = configs as any;
    const { ossConfig, accessKeyId, accessKeySecret, stsToken, host } = data;
    const { region, bucket, prefixDir } = ossConfig;
    const commitId = `${prefixDir}${this.encodeFileName(file.name, category)}`;
    const client = new OSS({
      region: region,
      accessKeyId: accessKeyId,
      accessKeySecret: accessKeySecret,
      bucket: bucket,
      stsToken: stsToken,
    });

    // 小文件单次 put，大文件分片上传 + 断连续传重试（见 oss-upload.ts / risks R13）
    const r1 = await putWithStrategy(client as unknown as OssLikeClient, commitId, file, { onProgress });
    r1.url = `https://${host}/${r1.name}`;
    return r1;
  }

  /**
   * @description 获取有效 url
   * @param url
   * @param ossKey
   */
  async getAvailabilityUrl(url: string, withThumbUrl?: { process: string }, ossKey?: OssKey, isPreview?: boolean) {
    if (ossKey === 'ctc') {
      const dUrl = url.replace(/%/g, '%25'); // 兼容数据库数据内的数据，需要手动将 URL 内的 % 转换为 %25
      if (withThumbUrl) {
        return `${dUrl}?x-oss-process=${withThumbUrl.process}`;
      }
      return dUrl;
    }
    if (isPreview) {
      return await this.signatureUrl(url, withThumbUrl, ossKey, true);
    }
    return await this.signatureUrl(url, withThumbUrl, ossKey, false);
  }

  /**
   * @description 获取临时加密 url
   * @param url 原始 url
   * @param withThumbUrl 缩略图配置
   */
  async signatureUrl(url: string, withThumbUrl?: { process: string }, ossKey?: OssKey, isPreview?: boolean) {
    try {
      await this.getUtoken();
      const configs = await this.getOssConfig(ossKey);
      const { data } = configs as any;
      const { ossConfig, accessKeyId, accessKeySecret, stsToken, host } = data;
      const { region, bucket } = ossConfig;
      const client = new OSS({
        region: region,
        accessKeyId: accessKeyId,
        accessKeySecret: accessKeySecret,
        bucket: bucket,
        stsToken: stsToken,
      });
      if (url === '') {
        return '';
      }
      const name = decodeURI(new URL(url.replace(/%/g, '%25')).pathname.substring(1));
      const fileName = this.parseFileName(name.slice(name.lastIndexOf('/') + 1));
      const res = new URL(
        client.signatureUrl(name, {
          expires: SIGNATURE_EXPIRES,
          process: withThumbUrl?.process,
          response: isPreview ? { 'content-disposition': 'inline' } : buildOssDownloadResponse(fileName),
        }),
      );
      res.protocol = 'https:';
      res.host = host;
      return res.href;
    } catch (error) {
      console.error(error);
      return '';
    }
  }

  /**
   * @description 批量加密 url
   * @param list url 数组
   * @returns 加密后的 url 数组
   */
  async signatureUrlBatch(
    urls: string[],
    withThumbUrl?: { process: string }, // 同时进行缩略图处理
    ossKey?: OssKey,
  ) {
    await this.getUtoken();
    const configs = await this.getOssConfig(ossKey);
    const { data } = configs as any;
    const { ossConfig, accessKeyId, accessKeySecret, stsToken, host } = data;
    const { region, bucket } = ossConfig;
    const client = new OSS({
      region: region,
      accessKeyId: accessKeyId,
      accessKeySecret: accessKeySecret,
      bucket: bucket,
      stsToken: stsToken,
    });
    const res = await Promise.all<string>(
      urls.map(
        (url) =>
          new Promise((resolve) => {
            try {
              const name = decodeURI(new URL(url.replace(/%/g, '%25')).pathname.substring(1));
              client
                .asyncSignatureUrl(name, {
                  expires: SIGNATURE_EXPIRES,
                  process: withThumbUrl?.process,
                })
                .then((r) => resolve(r));
            } catch (error) {
              console.error(error);
              resolve('');
            }
          }),
      ),
    );
    return res.map((url) => {
      try {
        const u = new URL(url);
        u.protocol = 'https:';
        u.host = host;
        return u.href;
      } catch (error) {
        return '';
      }
    });
  }

  /**
   * @description 批量加密 url
   * @param list 数据列表（对象数组）
   * @param keys 需要加密的字段，指定的字段会被修改
   * @param withThumbUrl 缩略图配置
   * @param ossKey oss key
   * @returns 原始数组
   */
  async signatureList(
    list: any[],
    keys: string[],
    withThumbUrl?: { process: string }, // 同时进行缩略图处理
    ossKey?: OssKey,
  ) {
    await this.getUtoken();
    const configs = await this.getOssConfig(ossKey);
    const { data } = configs as any;
    const { ossConfig, accessKeyId, accessKeySecret, stsToken, host } = data;
    const { region, bucket } = ossConfig;
    const client = new OSS({
      region: region,
      accessKeyId: accessKeyId,
      accessKeySecret: accessKeySecret,
      bucket: bucket,
      stsToken: stsToken,
    });
    const handleUrl = (url: string, process?: string, isDownload?: boolean) => {
      const name = decodeURI(new URL(url.replace(/%/g, '%25')).pathname.substring(1));
      const fileName = this.parseFileName(name.slice(name.lastIndexOf('/') + 1));
      const signatureOptions: any = {
        expires: 3600,
        process: process,
      };
      if (isDownload) {
        signatureOptions.response = buildOssDownloadResponse(fileName);
      }
      const res = new URL(client.signatureUrl(name, signatureOptions));
      res.protocol = 'https:';
      res.host = host;
      return res.toString();
    };
    const handler = (url: string) => {
      const res: { thumbUrl?: string; signatureUrl: string } = {
        signatureUrl: '',
      };
      if (withThumbUrl) {
        res.thumbUrl = handleUrl(url, withThumbUrl.process);
      }
      res.signatureUrl = handleUrl(url, undefined, !withThumbUrl);
      return res;
    };
    list.forEach((item) => {
      keys.forEach((key) => {
        try {
          if (!item[key]) {
            return;
          }
          if (Array.isArray(item[key])) {
            item[key] = item[key].map((url: string) => {
              return handler(url);
            });
          } else {
            item[key] = [handler(item[key])];
          }
        } catch (e) {
          console.warn(e);
        }
      });
    });
    return list;
  }
}

const ossUtil = new OssUtil();
export default ossUtil;
